<template>
  <div class="signature-pad">
    <div class="signature-wrap">
      <vue-signature ref="pad" :h="height" w="100%" :sig-option="sigOption" :disabled="disabled" />
      <span class="signature-hint">Sign here</span>
    </div>
    <b-button v-if="!disabled" variant="flat-secondary" size="sm" class="mt-25" @click="clear">Clear</b-button>
  </div>
</template>

<script>
// Thin wrapper around vue-signature that
//  * re-fits the canvas when the wizard tab it lives in becomes visible (vue-signature
//    sizes the canvas once on mount, which gives a 0px canvas inside a hidden tab),
//  * keeps the drawing across re-fits and window resizes,
//  * emits the PNG data URL (`input`) after every stroke and restores it from `value`
//    when the component is re-mounted (e.g. a person card gets a new key after save).
import vueSignature from "vue-signature";

export default {
  name: "SignaturePad",

  components: { vueSignature },

  props: {
    value: { type: String, default: null },
    active: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    height: { type: String, default: "160px" },
  },

  data() {
    return {
      sigOption: {
        backgroundColor: "rgb(255,255,255)",
        penColor: "rgb(0, 0, 0)",
        onEnd: () => this.emitValue(),
      },
    };
  },

  watch: {
    active(isActive) {
      if (isActive) this.$nextTick(() => this.fit());
    },
  },

  mounted() {
    // vue-signature draws on its own nextTick; wait one more before touching the pad
    this.$nextTick(() =>
      this.$nextTick(() => {
        this.fit();
        if (this.value && this.isEmpty()) this.restore(this.value);
      })
    );
    this.onResize = () => this.fit();
    window.addEventListener("resize", this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },

  methods: {
    pad() {
      const comp = this.$refs.pad;
      return comp && comp.sig && typeof comp.sig.clear === "function" ? comp.sig : null;
    },

    canvas() {
      const comp = this.$refs.pad;
      return comp && comp.$el ? comp.$el.querySelector("canvas") : null;
    },

    // Re-sizes the backing canvas to the displayed width, preserving the strokes.
    fit() {
      const sig = this.pad();
      const canvas = this.canvas();
      if (!sig || !canvas || !canvas.offsetWidth) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const width = Math.round(canvas.offsetWidth * ratio);
      const height = Math.round(parseInt(this.height, 10) * ratio);
      if (canvas.width === width && canvas.height === height) return;

      const data = sig.isEmpty() ? null : sig.toData();
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").scale(ratio, ratio);
      sig.clear();
      if (data && data.length) sig.fromData(data);
    },

    restore(dataUrl) {
      const sig = this.pad();
      if (!sig || !dataUrl) return;
      sig.fromDataURL(dataUrl);
    },

    emitValue() {
      this.$emit("input", this.save());
    },

    isEmpty() {
      const sig = this.pad();
      return !sig || sig.isEmpty();
    },

    save() {
      const sig = this.pad();
      if (!sig || sig.isEmpty()) return null;
      return sig.toDataURL("image/png");
    },

    clear() {
      const sig = this.pad();
      if (sig) sig.clear();
      this.$emit("input", null);
    },
  },
};
</script>
